import {
  CheckCircle2,
  CircleX,
  Download,
  FileText,
  Loader2,
  Lock,
  LogIn,
  ThumbsDown,
  ThumbsUp,
  UploadIcon,
  X,
  XCircle,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import { memo, useEffect, useState } from "react";
import { preconnect } from "react-dom";
import { useTranslation } from "react-i18next";
import { useSidePanel, type UseSidePanelOptions } from "../hooks/useSidePanel";
import { useLangPath } from "../hooks/useLangPath";
import { AboutCard } from "./AboutCard";
import { ApiCard } from "./ApiCard";
import { DirectionSwitcher } from "./DirectionSwitcher";
import { DonateCard } from "./DonateCard";
import { FaqCard } from "./FaqCard";
import { Footer } from "./Footer";
import { ProjectsCard } from "./ProjectsCard";
import { Button } from "~/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "~/components/ui/card";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
} from "~/components/ui/empty";
import { parseDirection, SCRIPT_LABEL_KEY } from "~/utils/direction";

type SidePanelProps = Omit<UseSidePanelOptions, "t">;

const ACCEPTED = [
  ".pdf",
  ".doc",
  ".docx",
  ".txt",
  ".xls",
  ".xlsx",
  ".ppt",
  ".pptx",
];
const MAX_BYTES = 10 * 1024 * 1024;

function formatBytes(b: number): string {
  return b >= 1048576
    ? `${(b / 1048576).toFixed(1)} MB`
    : `${Math.round(b / 1024)} KB`;
}

const JARNOMA_HOST = "https://jarnoma.obuna.com";

// The ad image is the mobile LCP element. jarnoma.js only arrives after
// hydration, so this inline script (right after the ad slot) starts the
// /serve request during HTML parse and paints the ad as soon as it returns.
// jarnoma.js later reuses the same promise from window.__jarnoma, sees
// data-jarnoma-ready and only records the impression. The render mirrors
// jarnoma.js's own render().
const JARNOMA_BOOTSTRAP = `(function () {
  var el = document.currentScript && document.currentScript.previousElementSibling;
  if (!el || !el.dataset.jarnoma || !window.fetch) return;
  var zone = el.dataset.jarnoma, site = el.dataset.site || "";
  var cache = (window.__jarnoma = window.__jarnoma || {});
  var key = zone + ":" + site;
  var served = cache[key] || (cache[key] = fetch("${JARNOMA_HOST}/serve?zone=" + encodeURIComponent(zone) + "&site=" + encodeURIComponent(site)).then(function (r) { return r.json(); }));
  served.then(function (data) {
    var ad = data && data.ad;
    if (!ad || el.dataset.jarnomaReady === "1") return;
    el.dataset.jarnomaReady = "1";
    var a = document.createElement("a");
    a.href = ad.click_url;
    a.target = "_blank";
    a.rel = "sponsored noopener noreferrer";
    a.style.display = "block";
    if (ad.html_content) {
      var div = document.createElement("div");
      div.innerHTML = ad.html_content;
      a.appendChild(div);
    } else {
      var img = document.createElement("img");
      img.src = ad.image_url;
      img.alt = ad.title || "";
      img.decoding = "async";
      img.fetchPriority = "high";
      if (ad.zone_width && ad.zone_height) {
        img.width = ad.zone_width;
        img.height = ad.zone_height;
      }
      img.style.cssText = "max-width:100%;display:block";
      a.appendChild(img);
    }
    el.appendChild(a);
  }, function () {});
})();`;

// Module-level on purpose: React 19 rewrites innerHTML whenever this prop
// object changes, which would wipe the painted ad on every re-render.
const AD_SLOT_HTML = { __html: "" };
const AD_BOOTSTRAP_HTML = { __html: JARNOMA_BOOTSTRAP };

// Memoized: the home page re-renders on every keystroke in the editor, and
// nothing in this panel depends on the input text.
export const SidePanel = memo(function SidePanel(props: SidePanelProps) {
  const { t } = useTranslation();
  const lp = useLangPath();

  // Rendered into <head> during SSR. /serve is a CORS fetch, the ad image is
  // not, so each needs its own connection.
  preconnect(JARNOMA_HOST, { crossOrigin: "anonymous" });
  preconnect(JARNOMA_HOST);

  const {
    readOnly,
    selectedDirection,
    handleFromLanguageChange,
    handleToLanguageChange,
    handleDirectionSwap,
    handleTranslit,
    isLoading,
    uploadState,
    subscriptionRequired,
    authRequired,
    fileInputRef,
    triggerFilePicker,
    resetUpload,
    startUpload,
    uploadingMessage,
    errorMessage,
    downloadUrl,
    handleDownload,
    isRated,
    ratingChoice,
    handleRating,
    cancelTranslation,
  } = useSidePanel({ ...props, t });

  // Pick -> validate -> preview -> confirm (no auto-upload).
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const validateAndSelect = (file: File) => {
    const name = file.name.toLowerCase();
    if (!ACCEPTED.some((e) => name.endsWith(e))) {
      setSelectedFile(null);
      setValidationError(
        t("sidePanel.badType", "Fayl turi qoʻllab-quvvatlanmaydi"),
      );
      return;
    }
    if (file.size > MAX_BYTES) {
      setSelectedFile(null);
      setValidationError(t("sidePanel.fileTooLarge"));
      return;
    }
    setValidationError(null);
    // Drop any previous result, otherwise the derived state stays on
    // "success"/"error" and this file never reaches the preview step.
    resetUpload();
    setSelectedFile(file);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) validateAndSelect(f);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) validateAndSelect(f);
  };

  const startTranslate = () => {
    if (!selectedFile) return;
    const f = selectedFile;
    setSelectedFile(null);
    startUpload(f);
  };

  useEffect(() => {
    if (document.querySelector('script[src*="jarnoma"]')) return;
    const s = document.createElement("script");
    s.src = "https://jarnoma.obuna.com/jarnoma.js";
    s.defer = true;
    document.head.appendChild(s);
  }, []);

  const { from, to } = parseDirection(selectedDirection);
  const directionLabel = `${t(SCRIPT_LABEL_KEY[from])} → ${t(SCRIPT_LABEL_KEY[to])}`;
  // sidePanel.successMessageLattocyr, ...Cyrtonew etc. — one key per direction.
  const successDirectionKey = `sidePanel.successMessage${selectedDirection.charAt(0).toUpperCase()}${selectedDirection.slice(1)}`;

  const fileState:
    | "uploading"
    | "success"
    | "error"
    | "subscribe"
    | "login"
    | "preview"
    | "idle" =
    uploadState === "uploading"
      ? "uploading"
      : uploadState === "success"
        ? "success"
        : // Guest's free quota is used up (server 401 auth_required) -> ask to log in.
          uploadState === "error" && authRequired
          ? "login"
          : uploadState === "error" && subscriptionRequired
            ? "subscribe"
            : uploadState === "error"
              ? "error"
              : // Guests may upload the first file without logging in; the backend
                // decides whether a login is required and returns auth_required.
                selectedFile
                ? "preview"
                : "idle";

  const hiddenFileInput = (
    <input
      ref={fileInputRef}
      type="file"
      className="hidden"
      onChange={onInputChange}
      accept={ACCEPTED.join(",")}
    />
  );

  return (
    <div className="options-container bg-gray-100 lg:w-[320px] shrink-0 p-4 flex flex-col gap-8 border-t lg:border-l overflow-y-auto lg:border-t-0">
      <div className="flex-col gap-4 hidden lg:flex">
        <h4 className="font-bold">{t("sidePanel.selectDirection")}</h4>
        <DirectionSwitcher
          selectedDirection={selectedDirection}
          onFromLanguageChange={handleFromLanguageChange}
          onToLanguageChange={handleToLanguageChange}
          onDirectionSwap={handleDirectionSwap}
          readOnly={readOnly}
        />
        <Button
          onClick={readOnly ? undefined : handleTranslit}
          disabled={readOnly || isLoading}
        >
          {isLoading ? t("sidePanel.loading") : t("sidePanel.translit")}
        </Button>
      </div>

      {!readOnly && (
        <Card className="shrink-0">
          <CardHeader>
            <CardTitle>{t("sidePanel.fileConversion")}</CardTitle>
          </CardHeader>
          <CardContent>
            {fileState === "login" && (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <LogIn />
                  </EmptyMedia>
                  <EmptyTitle>
                    {t(
                      "sidePanel.freeUsedLogin",
                      "Bepul tarjima ishlatildi. Davom etish uchun tizimga kiring.",
                    )}
                  </EmptyTitle>
                </EmptyHeader>
                <EmptyContent>
                  <Button asChild>
                    <Link to={lp("/login")}>
                      {t("sidePanel.login", "Kirish")}
                    </Link>
                  </Button>
                </EmptyContent>
              </Empty>
            )}

            {fileState === "subscribe" && (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Lock />
                  </EmptyMedia>
                  <EmptyTitle>
                    {t(
                      "sidePanel.subscriptionRequired",
                      "Fayl funksiyalari obuna talab qiladi",
                    )}
                  </EmptyTitle>
                </EmptyHeader>
                <EmptyContent>
                  <Button asChild>
                    <Link to={lp("/subscription")}>
                      {t("sidePanel.subscribe", "Obuna bo‘lish")}
                    </Link>
                  </Button>
                </EmptyContent>
              </Empty>
            )}


            {fileState === "idle" && (
              <>
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={onDrop}
                  onClick={triggerFilePicker}
                  className={`flex cursor-pointer flex-col items-center gap-2 rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
                    isDragging ? "border-blue-600 bg-blue-50" : "border-border"
                  }`}
                >
                  <UploadIcon className="text-muted-foreground" />
                  <p className="font-bold">
                    {t("sidePanel.dropOrBrowse", "Faylni torting yoki tanlang")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("sidePanel.fileSizeLimit")}
                  </p>
                </div>
                {hiddenFileInput}
                {validationError && (
                  <p className="mt-3 text-sm text-destructive">
                    {validationError}
                  </p>
                )}
              </>
            )}

            {fileState === "preview" && selectedFile && (
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-3 rounded-xl border p-3">
                  <FileText className="shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-bold">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {formatBytes(selectedFile.size)}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedFile(null)}
                    aria-label={t("sidePanel.remove", "Olib tashlash")}
                    className="rounded-md p-1 text-muted-foreground hover:bg-gray-100"
                  >
                    <X size={18} />
                  </button>
                </div>
                <p className="text-sm text-muted-foreground">
                  {t("sidePanel.direction", "Yo‘nalish")}:{" "}
                  <span className="font-bold">{directionLabel}</span>
                </p>
                <Button onClick={startTranslate}>
                  {t("sidePanel.translateFile", "Tarjima qilish")}
                </Button>
              </div>
            )}

            {fileState === "uploading" && (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <Loader2 className="animate-spin" />
                  </EmptyMedia>
                  <EmptyTitle>{uploadingMessage}</EmptyTitle>
                </EmptyHeader>
                <EmptyContent>
                  <Button variant="destructive" onClick={cancelTranslation}>
                    <XCircle />
                    {t("sidePanel.cancel", "Bekor qilish")}
                  </Button>
                </EmptyContent>
              </Empty>
            )}

            {fileState === "error" && (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <CircleX />
                  </EmptyMedia>
                  <EmptyTitle>
                    {errorMessage ||
                      t("sidePanel.uploadError", "Xatolik yuz berdi")}
                  </EmptyTitle>
                </EmptyHeader>
                <EmptyContent>
                  {hiddenFileInput}
                  <Button onClick={triggerFilePicker}>
                    {t("sidePanel.retry", "Qayta urinish")}
                  </Button>
                </EmptyContent>
              </Empty>
            )}

            {fileState === "success" && (
              <Empty>
                <EmptyHeader>
                  <EmptyMedia variant="icon">
                    <CheckCircle2 />
                  </EmptyMedia>
                  <EmptyTitle>
                    {t("sidePanel.successMessage", {
                      direction: t(successDirectionKey),
                    })}
                  </EmptyTitle>
                </EmptyHeader>
                <EmptyContent>
                  {downloadUrl && (
                    <Button className="w-full" onClick={handleDownload}>
                      {t("sidePanel.download")} <Download />
                    </Button>
                  )}
                  <p className="text-sm text-muted-foreground">
                    {t("sidePanel.rateTranslation")}
                  </p>
                  <div className="grid grid-cols-2 gap-4 w-full">
                    <Button
                      variant="outline"
                      className={`w-full ${isRated && ratingChoice === "yes" ? "bg-green-500 text-white" : ""}`}
                      onClick={() => handleRating("yes")}
                      disabled={isRated && ratingChoice === "yes"}
                      aria-label={t("aria.like")}
                    >
                      <ThumbsUp />
                    </Button>
                    <Button
                      variant="outline"
                      className={`w-full ${isRated && ratingChoice === "no" ? "bg-red-500 text-white" : ""}`}
                      onClick={() => handleRating("no")}
                      disabled={isRated && ratingChoice === "no"}
                      aria-label={t("aria.feedback")}
                    >
                      <ThumbsDown />
                    </Button>
                  </div>
                  {hiddenFileInput}
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={triggerFilePicker}
                  >
                    {t("sidePanel.uploadAnother", "Boshqa fayl yuklash")}
                  </Button>
                </EmptyContent>
              </Empty>
            )}
          </CardContent>
        </Card>
      )}

      <div style={{ textAlign: "center", margin: "0 auto", maxWidth: 640 }}>
        {/* Filled by the script below and by jarnoma.js, never by React:
            the empty innerHTML keeps React from touching the painted ad */}
        <div
          data-jarnoma="global"
          data-site="lotin"
          style={{ borderRadius: "0.75rem", overflow: "hidden" }}
          dangerouslySetInnerHTML={AD_SLOT_HTML}
          suppressHydrationWarning
        />
        <script dangerouslySetInnerHTML={AD_BOOTSTRAP_HTML} />
      </div>
      <DonateCard />
      <ApiCard />
      <FaqCard />
      <AboutCard />
      <ProjectsCard />
      <Footer />
    </div>
  );
});
